import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, KeyRound } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  usePageTitle('Esqueci a Senha');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: email, 2: code + new password
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Código de redefinição enviado para seu email!');
        setStep(2);
      } else {
        toast.error(data.detail || 'Erro ao solicitar código');
      }
    } catch (error) {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Validações
    if (newPassword.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password?email=${encodeURIComponent(email)}&reset_code=${resetCode}&new_password=${encodeURIComponent(newPassword)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Senha redefinida com sucesso! Faça login com sua nova senha.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(data.detail || 'Erro ao redefinir senha');
      }
    } catch (error) {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1rem'
    }}>
      <Card style={{ width: '100%', maxWidth: '450px' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <KeyRound size={48} style={{ color: '#2563eb' }} />
          </div>
          <CardTitle style={{ fontSize: '1.75rem' }}>
            {step === 1 ? 'Esqueci a Senha' : 'Redefinir Senha'}
          </CardTitle>
          <CardDescription>
            {step === 1 
              ? 'Digite seu email para receber o código de redefinição'
              : 'Digite o código recebido e sua nova senha'
            }
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleRequestCode}>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="email">Email</Label>
                <div style={{ position: 'relative' }}>
                  <Mail 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#94a3b8' 
                    }} 
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{ paddingLeft: '2.5rem' }}
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                style={{ width: '100%', marginBottom: '1rem' }}
                disabled={loading}
                data-testid="request-reset-code-button"
              >
                {loading ? 'Enviando...' : 'Enviar Código'}
              </Button>

              <div style={{ textAlign: 'center' }}>
                <Link to="/login" style={{ color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ArrowLeft size={16} />
                  Voltar para Login
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="resetCode">Código de Verificação</Label>
                <div style={{ position: 'relative' }}>
                  <KeyRound 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#94a3b8' 
                    }} 
                  />
                  <Input
                    id="resetCode"
                    type="text"
                    placeholder="000000"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    maxLength={6}
                    style={{ paddingLeft: '2.5rem', fontSize: '1.25rem', letterSpacing: '0.25rem', textAlign: 'center' }}
                    data-testid="reset-code-input"
                  />
                </div>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                  Digite o código de 6 dígitos enviado para {email}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="newPassword">Nova Senha</Label>
                <div style={{ position: 'relative' }}>
                  <Lock 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#94a3b8' 
                    }} 
                  />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    style={{ paddingLeft: '2.5rem' }}
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                <div style={{ position: 'relative' }}>
                  <Lock 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#94a3b8' 
                    }} 
                  />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Digite novamente"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    style={{ paddingLeft: '2.5rem' }}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                style={{ width: '100%', marginBottom: '1rem' }}
                disabled={loading}
                data-testid="reset-password-button"
              >
                {loading ? 'Redefinindo...' : 'Redefinir Senha'}
              </Button>

              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#2563eb', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Solicitar novo código
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
